import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/shared/components/ui/button'
import { Dialog, DialogContent, DialogTitle } from '@/shared/components/ui/dialog'
import { buildApiUrl } from '@/shared/lib/api-origin'

interface ExampleGalleryProps {
  urls: string[]
  label: string
}

/**
 * Local storage returns root-relative `/media/...` paths, which 404 against the
 * SPA's own origin in dev; GCS returns absolute signed URLs. Resolve the former
 * against the API origin (a no-op in deployed envs, where the origin is empty).
 */
function resolveUrl(url: string): string {
  return url.startsWith('http://') || url.startsWith('https://')
    ? url
    : buildApiUrl(url)
}

/**
 * "This is what to photograph" thumbnails for a capture field.
 *
 * The images come from the tenant's evidence-request template
 * (`requirements[].example_media_urls`), uploaded once by an admin. Shared so
 * the evidence-upload page and the consumer claim form show the same examples
 * rather than each surface carrying its own copy.
 */
export function ExampleGallery({ urls, label }: ExampleGalleryProps) {
  const { t } = useTranslation()
  const [selected, setSelected] = useState<string | null>(null)
  const resolved = useMemo(() => urls.map(resolveUrl), [urls])

  if (resolved.length === 0) return null

  return (
    <>
      <div className="mb-3">
        <p className="clara-small mb-1.5 text-[var(--ink-3)]">
          {t('evidence.example', { defaultValue: 'Example' })}
        </p>
        <div className="flex gap-2 overflow-x-auto pb-1">
          {resolved.map((url, i) => (
            <Button
              key={url}
              variant="ghost"
              size="icon"
              onClick={() => setSelected(url)}
              className="h-auto w-auto flex-none overflow-hidden rounded-md border-[0.5px] border-[var(--rule)] p-0 transition-colors duration-150 hover:border-[var(--green-border)]"
            >
              <img
                src={url}
                alt={t('evidence.exampleAlt', {
                  defaultValue: 'Example {{number}} for {{label}}',
                  number: i + 1,
                  label,
                })}
                className="h-16 w-16 object-cover"
              />
            </Button>
          ))}
        </div>
      </div>

      <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
        <DialogContent className="max-w-sm overflow-hidden p-0">
          <DialogTitle className="sr-only">
            {t('evidence.exampleDialogTitle', {
              defaultValue: 'Example - {{label}}',
              label,
            })}
          </DialogTitle>
          {selected && (
            <img
              src={selected}
              alt={t('evidence.exampleAltSingle', {
                defaultValue: 'Example for {{label}}',
                label,
              })}
              className="w-full"
            />
          )}
          <p className="clara-small px-4 pb-4 text-center text-[var(--ink-3)]">
            {t('evidence.exampleCaption', {
              defaultValue: 'Example of what to capture',
            })}
          </p>
        </DialogContent>
      </Dialog>
    </>
  )
}
